import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button } from '.';
import useIndexes from '../hooks/indexes';
import useJobs from '../hooks/jobs';
import { Index, IndexBuildStatus, Job } from '../types';
import {
  INDEX_REBUILD_STARTED_EVENT,
  announceIndexRebuild,
  describeIndexBuildProgress,
  formatNumber,
} from '../utilities/jobs';

const POLL_INTERVAL = 2000;

type IndexBuildAlertProps = {
  listId: string;
  index: Index | null;
  onIndexChange: (index: Index) => void;
};

/**
 * Shows an index's build status, keeps it up to date while the index is
 * being built, and offers to retry a build that failed
 *
 * Polling uses its own hook instances, so it doesn't set the page's pending
 * state (and flash the loading overlay) every couple of seconds
 */
export function IndexBuildAlert({
  listId,
  index,
  onIndexChange,
}: IndexBuildAlertProps): ReactElement {
  const { index: indexJobs } = useJobs();
  const { show: showIndex } = useIndexes();
  const {
    pending: retrying,
    error: retryError,
    resetError: resetRetryError,
    rebuild: rebuildIndex,
  } = useIndexes();

  const [job, setJob] = useState<Job | null>(null);
  const [justBuilt, setJustBuilt] = useState(false);
  const previousBuildStatus = useRef<IndexBuildStatus | undefined>(undefined);

  const indexId = index?.id;
  const buildStatus = index?.buildStatus;

  const fetchLatestJob = useCallback(
    async (signal?: AbortSignal): Promise<Job | null> => {
      if (!indexId) {
        return null;
      }

      const response = await indexJobs(
        {
          type: 'index.build',
          indexId,
          page: 1,
          limit: 1,
          order: 'createdAt',
          direction: 'desc',
        },
        signal
      );
      const latest = response?.data?.[0] ?? null;

      if (!signal?.aborted) {
        setJob(latest);
      }

      return latest;
    },
    [indexJobs, indexId]
  );

  // While the index is building, follow its latest build job. Once that job
  // has stopped, fetch the index again to see where it ended up; a build that
  // was superseded leaves the index building, so keep following it
  useEffect(() => {
    if (!indexId || buildStatus !== 'building') {
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      const latest = await fetchLatestJob(signal);

      if (signal.aborted) {
        return;
      }

      if (latest && ['queued', 'running'].includes(latest.status)) {
        timeout = setTimeout(poll, POLL_INTERVAL);
        return;
      }

      const refreshed = await showIndex(listId, indexId, signal);

      if (signal.aborted || !refreshed) {
        return;
      }

      if (refreshed.buildStatus === 'building') {
        timeout = setTimeout(poll, POLL_INTERVAL);
      } else {
        onIndexChange(refreshed);
      }
    };

    poll();

    return () => {
      abortController.abort();
      clearTimeout(timeout);
    };
  }, [listId, indexId, buildStatus, fetchLatestJob, showIndex, onIndexChange]);

  // A failed build's job says why it failed
  useEffect(() => {
    if (buildStatus !== 'failed') {
      return;
    }

    const abortController = new AbortController();

    fetchLatestJob(abortController.signal);

    return () => abortController.abort();
  }, [buildStatus, fetchLatestJob]);

  // Only say an index is ready if it finished building while we watched
  useEffect(() => {
    if (previousBuildStatus.current === 'building' && buildStatus === 'ready') {
      setJustBuilt(true);
      fetchLatestJob();
    }

    if (buildStatus === 'building') {
      setJustBuilt(false);
    }

    previousBuildStatus.current = buildStatus;
  }, [buildStatus, fetchLatestJob]);

  const handleRetry = useCallback(async () => {
    if (!indexId) {
      return;
    }

    const response = await rebuildIndex(listId, indexId);

    if (response) {
      announceIndexRebuild(response);
    }
  }, [rebuildIndex, listId, indexId]);

  // The rebuild might have been started here or from the jobs menu
  useEffect(() => {
    const handleRebuildStarted = (event: Event) => {
      const rebuilt = (event as CustomEvent<Index>).detail;

      if (rebuilt.id === indexId) {
        onIndexChange(rebuilt);
      }
    };

    window.addEventListener(INDEX_REBUILD_STARTED_EVENT, handleRebuildStarted);

    return () =>
      window.removeEventListener(
        INDEX_REBUILD_STARTED_EVENT,
        handleRebuildStarted
      );
  }, [indexId, onIndexChange]);

  const result = job?.status === 'completed' ? job.result : null;

  return (
    <>
      <Alert intent="info" show={buildStatus === 'building'} loading>
        <p>
          <strong>This index is being built.</strong>{' '}
          {describeIndexBuildProgress(job)}
        </p>
        <p>
          Filtering, sorting, alias lookups and searches that use it are refused
          until it's ready.
        </p>
      </Alert>
      <Alert intent="danger" show={buildStatus === 'failed'}>
        <p>
          <strong>This index failed to build.</strong>{' '}
          {job?.error?.message ?? ''}
        </p>
        <p>
          Filtering, sorting, alias lookups and searches that use it are refused
          until it's rebuilt. Fix the problem, then rebuild it.
        </p>
        <Button
          intent="danger"
          icon={faRefresh}
          label="Rebuild"
          onClick={handleRetry}
          loading={retrying}
          disabled={retrying || !!index?.locked}
          secondary
        />
      </Alert>
      <Alert
        intent="danger"
        show={!!retryError}
        closable
        onClose={resetRetryError}
      >
        <p>{retryError?.message}</p>
      </Alert>
      <Alert
        intent="success"
        show={justBuilt && buildStatus === 'ready'}
        closable
        onClose={() => setJustBuilt(false)}
      >
        <p>
          <strong>This index is ready.</strong>
          {result &&
            ` ${formatNumber(result.indexed)} of ${formatNumber(
              result.processed
            )} items have a value at its pointer.`}
        </p>
      </Alert>
    </>
  );
}
